import {baseUrl} from '@/constants/app'
import authService from '@/services/auth/authService'
import helper from '@/resources/helpers/helpers'

const INLINE_MIME = /^(application\/pdf$|image\/|text\/plain$)/

async function fetchDocument(path) {
	const token = authService.accessToken()
	const platform = authService.activePlatform()

	const headers = {
		Authorization: `Bearer ${token}`,
		Accept: 'application/json',
	}

	if (platform) {
		headers['X-PLATFORM'] = platform
	}

	const res = await fetch(baseUrl + path, {headers})

	if (!res.ok) {
		throw new Error(`HTTP ${res.status}`)
	}

	return res.blob()
}

function saveBlobAs(blobUrl, filename) {
	const link = document.createElement('a')
	link.href = blobUrl
	link.download = filename
	document.body.appendChild(link)
	link.click()
	link.remove()
}

/**
 * Abre un documento protegido del backend.
 *
 * Los documentos ya no se sirven como ficheros estáticos públicos: el endpoint
 * exige el guard auth:api. Por eso hay que pedirlos con el token (Authorization)
 * y la plataforma activa (X-PLATFORM, para que el backend seleccione la BD
 * correcta vhe/gei), recibir el binario como blob y abrirlo en una pestaña nueva.
 *
 * Se envía Accept: application/json para que un token caducado devuelva 401
 * (res.ok === false -> toast) en lugar de un 302 a /login que fetch seguiría
 * silenciosamente.
 *
 * Una blob: URL no lleva nombre de fichero: si el navegador no sabe representar
 * el tipo en pestaña lo descarga usando el UUID de la blob URL. Por eso los
 * tipos no representables se fuerzan con <a download> y el nombre real. Para los
 * que sí representa (PDF, imágenes) el nombre correcto lo da downloadDocument.
 *
 * @param {string} path Ruta del documento devuelta por el backend (p.ej.
 *                       "storage/documents_vhe_installations/Datos/.../fichero.pdf").
 * @param {string} [name] Nombre original del fichero, usado al descargar.
 */
export async function openDocument(path, name) {
	if (!path) {
		return
	}

	try {
		const blob = await fetchDocument(path)
		const blobUrl = URL.createObjectURL(blob)

		if (INLINE_MIME.test(blob.type.split(';')[0].trim())) {
			window.open(blobUrl, '_blank')
		} else {
			saveBlobAs(blobUrl, name || path.split('/').pop())
		}

		setTimeout(() => URL.revokeObjectURL(blobUrl), 30000)
	} catch (e) {
		helper.showToast('No se pudo abrir el documento', 'AlertTriangleIcon', 'danger')
	}
}

/**
 * Descarga el documento con su nombre original, sea cual sea su tipo.
 *
 * @param {string} path Ruta del documento devuelta por el backend.
 * @param {string} [name] Nombre original del fichero.
 */
export async function downloadDocument(path, name) {
	if (!path) {
		return
	}

	try {
		const blobUrl = URL.createObjectURL(await fetchDocument(path))

		saveBlobAs(blobUrl, name || path.split('/').pop())

		setTimeout(() => URL.revokeObjectURL(blobUrl), 30000)
	} catch (e) {
		helper.showToast('No se pudo descargar el documento', 'AlertTriangleIcon', 'danger')
	}
}

export default {openDocument, downloadDocument}
